import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import { FiSave, FiSearch, FiPlus, FiZap, FiEdit3, FiTrash2, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';

const TrendingManager = ({ initialSection = 'featured' }) => {
    const toast = useToast();
    const [activeSection, setActiveSection] = useState(initialSection);
    const [config, setConfig] = useState({
        maxCards: 4,
        mode: 'automatic',
        productIds: []
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [sections, setSections] = useState([]);

    // Fetch sections on mount
    useEffect(() => {
        fetchSections();
    }, []);

    useEffect(() => {
        if (sections.length > 0) {
            fetchConfig(activeSection);
        }
    }, [activeSection, sections]);

    const fetchSections = async () => {
        try {
            const response = await apiClient.get('/trending/sections');
            setSections(response.data.sections || []);
        } catch (error) {
            console.error('Error fetching sections:', error);
            setSections([
                { id: 'featured', name: 'Featured Products', isDefault: true },
                { id: 'stationary', name: 'Stationary' },
                { id: 'textiles', name: 'Textiles & Fabrics' },
                { id: 'pottery', name: 'Pottery & Ceramics' },
                { id: 'handicrafts', name: 'Handicrafts' },
                { id: 'electronics', name: 'Electronics' },
                { id: 'home-decor', name: 'Home Decor' }
            ]);
        }
    };

    const fetchConfig = async (sectionId) => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/trending/${sectionId}`);
            setConfig(response.data.config);

            if (response.data.products) {
                setSelectedProducts(response.data.products);
            } else {
                setSelectedProducts([]);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching config:', error);
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = sessionStorage.getItem('adminToken');
            await apiClient.post('/trending', {
                sectionId: activeSection,
                ...config,
                productIds: selectedProducts.map(p => p.id)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Configuration saved successfully!');
            setSaving(false);
        } catch (error) {
            console.error('Error saving config:', error);
            toast.error('Failed to save configuration');
            setSaving(false);
        }
    };

    const searchProducts = async () => {
        if (!searchQuery.trim()) return;
        try {
            const response = await apiClient.get(`/products?search=${searchQuery}&limit=10`);
            setSearchResults(response.data.products || []);
        } catch (error) {
            console.error('Error searching products:', error);
        }
    };

    const addProduct = (product) => {
        if (selectedProducts.find(p => p.id === product.id)) return;
        if (selectedProducts.length >= config.maxCards) {
            toast.warning(`You can only select up to ${config.maxCards} products.`);
            return;
        }
        setSelectedProducts([...selectedProducts, product]);
    };

    const removeProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    };

    const moveProduct = (productId, direction) => {
        const index = selectedProducts.findIndex(p => p.id === productId);
        if (index === -1) return;

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= selectedProducts.length) return;

        const newProducts = [...selectedProducts];
        [newProducts[index], newProducts[newIndex]] = [newProducts[newIndex], newProducts[index]];
        setSelectedProducts(newProducts);
    };

    return (
        <div className="trending-manager">
            {/* Section Tabs */}
            <div className="section-selector">
                <div className="section-header">
                    <div>
                        <h3>Select Section</h3>
                        <span className="section-hint">Choose which homepage section to configure</span>
                    </div>
                </div>

                <div className="tabs-container">
                    {sections.map(section => (
                        <button
                            key={section.id}
                            className={`tab-btn ${activeSection === section.id ? 'active' : ''}`}
                            onClick={() => setActiveSection(section.id)}
                        >
                            {section.name}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="loading-state">Loading configuration...</div>
            ) : (
                <div className="config-panel">
                    {/* Cards Count */}
                    <div className="config-card">
                        <div className="config-row">
                            <div className="config-label">
                                <span className="label-title">Number of Cards</span>
                                <span className="label-hint">Maximum products visible in this section</span>
                            </div>
                            <div className="number-input-wrapper">
                                <button
                                    className="number-btn"
                                    onClick={() => setConfig({ ...config, maxCards: Math.max(1, config.maxCards - 1) })}
                                >
                                    −
                                </button>
                                <input
                                    type="number"
                                    min="1"
                                    max="12"
                                    value={config.maxCards}
                                    onChange={(e) => setConfig({ ...config, maxCards: parseInt(e.target.value) || 1 })}
                                />
                                <button
                                    className="number-btn"
                                    onClick={() => setConfig({ ...config, maxCards: Math.min(12, config.maxCards + 1) })}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mode Selection */}
                    <div className="config-card">
                        <div className="config-label">
                            <span className="label-title">Selection Mode</span>
                            <span className="label-hint">How products are selected for this section</span>
                        </div>
                        <div className="mode-buttons">
                            <button
                                className={`mode-btn ${config.mode === 'automatic' ? 'active' : ''}`}
                                onClick={() => setConfig({ ...config, mode: 'automatic' })}
                            >
                                <div className="mode-icon"><FiZap /></div>
                                <div className="mode-content">
                                    <span className="mode-title">Automatic</span>
                                    <span className="mode-desc">Shows latest products automatically</span>
                                </div>
                            </button>
                            <button
                                className={`mode-btn ${config.mode === 'manual' ? 'active' : ''}`}
                                onClick={() => setConfig({ ...config, mode: 'manual' })}
                            >
                                <div className="mode-icon"><FiEdit3 /></div>
                                <div className="mode-content">
                                    <span className="mode-title">Manual</span>
                                    <span className="mode-desc">Hand-pick specific products</span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Manual Selection Panel */}
                    {config.mode === 'manual' && (
                        <div className="config-card manual-panel">
                            <div className="config-label">
                                <span className="label-title">Selected Products</span>
                                <span className="label-hint">{selectedProducts.length} of {config.maxCards} products selected</span>
                            </div>

                            {/* Selected Products List */}
                            <div className="selected-products">
                                {selectedProducts.length === 0 ? (
                                    <div className="empty-selection">
                                        <span>No products selected yet. Search and add products below.</span>
                                    </div>
                                ) : (
                                    selectedProducts.map((product, index) => (
                                        <div key={product.id} className="selected-product-card">
                                            <span className="product-order">{index + 1}</span>
                                            <img src={product.images?.[0]} alt={product.name} />
                                            <div className="product-info">
                                                <span className="product-name">{product.name}</span>
                                                <span className="product-price">₹{product.price?.toLocaleString('en-IN')}</span>
                                            </div>
                                            <div className="product-actions">
                                                <div className="reorder-buttons">
                                                    <button
                                                        className="reorder-btn"
                                                        onClick={() => moveProduct(product.id, 'up')}
                                                        disabled={index === 0}
                                                        title="Move up"
                                                    >
                                                        <FiChevronUp size={18} />
                                                    </button>
                                                    <button
                                                        className="reorder-btn"
                                                        onClick={() => moveProduct(product.id, 'down')}
                                                        disabled={index === selectedProducts.length - 1}
                                                        title="Move down"
                                                    >
                                                        <FiChevronDown size={18} />
                                                    </button>
                                                </div>
                                                <button
                                                    className="remove-btn"
                                                    onClick={() => removeProduct(product.id)}
                                                    title="Remove product"
                                                >
                                                    <FiTrash2 size={16} />
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Search Section */}
                            <div className="search-section">
                                <div className="search-input-wrapper">
                                    <input
                                        type="text"
                                        placeholder="Search products to add..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && searchProducts()}
                                    />
                                    <button className="search-btn" onClick={searchProducts}>
                                        <FiSearch size={16} />
                                        Search
                                    </button>
                                </div>

                                {searchResults.length > 0 && (
                                    <div className="search-results">
                                        {searchResults.map(product => (
                                            <div key={product.id} className="search-result-item">
                                                <img src={product.images?.[0]} alt={product.name} />
                                                <div className="result-info">
                                                    <span className="result-name">{product.name}</span>
                                                    <span className="result-price">₹{product.price?.toLocaleString('en-IN')}</span>
                                                </div>
                                                <button
                                                    className="add-btn"
                                                    onClick={() => addProduct(product)}
                                                    disabled={selectedProducts.find(p => p.id === product.id)}
                                                >
                                                    {selectedProducts.find(p => p.id === product.id) ? 'Added' : <><FiPlus /> Add</>}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Save Button */}
                    <button className="save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : <><FiSave /> Save Configuration</>}
                    </button>
                </div>
            )}

            <style jsx>{`
                .trending-manager {
                    padding: 24px;
                    background: #f8f9fa;
                    min-height: calc(100vh - 200px);
                }

                /* Section Selector */
                .section-selector {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 24px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                }
                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 16px;
                }
                .section-header h3 {
                    margin: 0 0 4px 0;
                    font-size: 1.1rem;
                    color: #1a1a1a;
                }
                .section-hint {
                    font-size: 0.85rem;
                    color: #666;
                }

                .tabs-container {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .tab-btn {
                    padding: 10px 18px;
                    border: 2px solid #e0e0e0;
                    background: white;
                    border-radius: 25px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-weight: 500;
                    color: #555;
                }
                .tab-btn:hover {
                    border-color: #1976d2;
                    background: #f5f9ff;
                }
                .tab-btn.active {
                    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
                    color: white;
                    border-color: #1976d2;
                    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
                }

                /* Loading State */
                .loading-state {
                    background: white;
                    padding: 40px;
                    text-align: center;
                    border-radius: 12px;
                    color: #666;
                }

                /* Config Panel */
                .config-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                .config-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                }
                .config-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .config-label {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    margin-bottom: 16px;
                }
                .config-row .config-label {
                    margin-bottom: 0;
                }
                .label-title {
                    font-weight: 600;
                    color: #1a1a1a;
                    font-size: 1rem;
                }
                .label-hint {
                    font-size: 0.85rem;
                    color: #666;
                }

                /* Number Input */
                .number-input-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 0;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    overflow: hidden;
                }
                .number-input-wrapper input {
                    width: 60px;
                    text-align: center;
                    padding: 10px;
                    border: none;
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #1a1a1a;
                }
                .number-input-wrapper input:focus {
                    outline: none;
                }
                .number-btn {
                    width: 40px;
                    height: 40px;
                    border: none;
                    background: #f5f5f5;
                    cursor: pointer;
                    font-size: 1.2rem;
                    color: #555;
                    transition: all 0.2s;
                }
                .number-btn:hover {
                    background: #1976d2;
                    color: white;
                }

                /* Mode Buttons */
                .mode-buttons {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }
                .mode-btn {
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    padding: 20px;
                    border: 2px solid #e0e0e0;
                    background: white;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: left;
                }
                .mode-btn:hover {
                    border-color: #1976d2;
                    background: #f5f9ff;
                }
                .mode-btn.active {
                    border-color: #1976d2;
                    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
                }
                .mode-icon {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f5f5f5;
                    border-radius: 12px;
                    font-size: 1.4rem;
                    color: #666;
                    flex-shrink: 0;
                }
                .mode-btn.active .mode-icon {
                    background: #1976d2;
                    color: white;
                }
                .mode-content {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .mode-title {
                    font-weight: 600;
                    font-size: 1rem;
                    color: #1a1a1a;
                }
                .mode-desc {
                    font-size: 0.85rem;
                    color: #666;
                }

                /* Manual Selection Panel */
                .manual-panel {
                    border: 2px solid #e3f2fd;
                }
                .selected-products {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    margin-bottom: 20px;
                }
                .empty-selection {
                    padding: 30px;
                    text-align: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    color: #666;
                    font-size: 0.9rem;
                }
                .selected-product-card {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    border: 1px solid #e0e0e0;
                }
                .product-order {
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #1976d2;
                    color: white;
                    border-radius: 50%;
                    font-weight: 600;
                    font-size: 0.85rem;
                    flex-shrink: 0;
                }
                .selected-product-card img {
                    width: 50px;
                    height: 50px;
                    object-fit: cover;
                    border-radius: 8px;
                    flex-shrink: 0;
                }
                .product-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .product-name {
                    font-weight: 500;
                    color: #1a1a1a;
                    font-size: 0.95rem;
                }
                .product-price {
                    font-size: 0.85rem;
                    color: #2e7d32;
                    font-weight: 600;
                }
                .product-actions {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-left: auto;
                    flex-shrink: 0;
                }
                .reorder-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .reorder-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 24px;
                    background: #e3f2fd;
                    border: 1px solid #90caf9;
                    border-radius: 6px;
                    color: #1976d2;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .reorder-btn:hover:not(:disabled) {
                    background: #1976d2;
                    border-color: #1976d2;
                    color: white;
                }
                .reorder-btn:disabled {
                    background: #f5f5f5;
                    border-color: #e0e0e0;
                    color: #bdbdbd;
                    cursor: not-allowed;
                }
                .remove-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 14px;
                    background: #fff5f5;
                    border: 1px solid #feb2b2;
                    border-radius: 8px;
                    color: #c53030;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.85rem;
                    font-weight: 500;
                    flex-shrink: 0;
                }
                .remove-btn:hover {
                    background: #c53030;
                    border-color: #c53030;
                    color: white;
                }

                /* Search Section */
                .search-section {
                    border-top: 1px solid #e0e0e0;
                    padding-top: 20px;
                }
                .search-input-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 0;
                    background: white;
                    border: 2px solid #e0e0e0;
                    border-radius: 12px;
                    margin-bottom: 16px;
                    overflow: hidden;
                    transition: border-color 0.2s;
                }
                .search-input-wrapper:focus-within {
                    border-color: #1976d2;
                }
                .search-input-wrapper input {
                    flex: 1;
                    border: none;
                    background: transparent;
                    padding: 12px 16px;
                    font-size: 0.95rem;
                }
                .search-input-wrapper input:focus {
                    outline: none;
                }
                .search-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    background: #1976d2;
                    color: white;
                    border: none;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .search-btn:hover {
                    background: #1565c0;
                }
                .search-results {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    max-height: 300px;
                    overflow-y: auto;
                }
                .search-result-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: white;
                    border: 1px solid #e0e0e0;
                    border-radius: 10px;
                    transition: all 0.2s;
                }
                .search-result-item:hover {
                    border-color: #1976d2;
                    background: #f5f9ff;
                }
                .search-result-item img {
                    width: 50px;
                    height: 50px;
                    object-fit: cover;
                    border-radius: 8px;
                    flex-shrink: 0;
                }
                .result-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .result-name {
                    font-weight: 500;
                    color: #1a1a1a;
                    font-size: 0.95rem;
                }
                .result-price {
                    font-size: 0.85rem;
                    color: #2e7d32;
                    font-weight: 600;
                }
                .add-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 16px;
                    background: #e8f5e9;
                    color: #2e7d32;
                    border: 1px solid #a5d6a7;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .add-btn:hover:not(:disabled) {
                    background: #2e7d32;
                    color: white;
                    border-color: #2e7d32;
                }
                .add-btn:disabled {
                    background: #f5f5f5;
                    color: #999;
                    border-color: #e0e0e0;
                    cursor: default;
                }

                /* Save Button */
                .save-btn {
                    align-self: flex-start;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 14px 28px;
                    background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    cursor: pointer;
                    font-weight: 600;
                    font-size: 1rem;
                    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
                    transition: all 0.2s;
                }
                .save-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(46, 125, 50, 0.4);
                }
                .save-btn:disabled {
                    background: #a5d6a7;
                    cursor: not-allowed;
                    box-shadow: none;
                    transform: none;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .mode-buttons {
                        grid-template-columns: 1fr;
                    }
                    .config-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .config-row .config-label {
                        margin-bottom: 0;
                    }
                    .section-header {
                        flex-direction: column;
                        gap: 12px;
                    }
                }
            `}</style>
        </div>
    );
};

export default TrendingManager;
